import { SkeletonLoader } from '@design/components/skeleton-loader/SkeletonLoader';
import { InsightDrawerHeaderContent } from '@terminal/components/insight-drawer-header/InsightDrawerHeaderContent';
import { InsightIconsRoot } from '@terminal/components/insight-icons-root/InsightIconsRoot';
import { useGetInsightWidgetData } from '@terminal/react-query-hooks/insights/useGetInsightWidgetData';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import { CardType } from '@terminal/views/insight-feed/components/insight-feed-card/InsightFeedCard';
import { InsightDrawerCategoryRow } from '@terminal/widgets/insight-widget/components/insight-drawer/components/insights-drawer-content/InsightDrawerCategoryRow';
import { LegacyInsightsDrawerContent } from '@terminal/widgets/insight-widget/components/insight-drawer/components/insights-drawer-content/LegacyInsightsDrawerContent';
import { IconConfig } from '../ScenarioInsightsLegacyDrawer';
import styles from './InsightsInfo.module.scss';

export interface InsightsInfoProps {
  insightId: string | null;
  iconConfig?: IconConfig;
}

export const InsightsInfo = ({ insightId, iconConfig }: InsightsInfoProps) => {
  const isInsightDrawerHeaderEnabled = useFlags(state =>
    state.getFeatureFlagState('PLATFORM-4942-insight-drawer-header')
  );
  const { data, isLoading } = useGetInsightWidgetData(insightId as string);

  if (isLoading) {
    return (
      <div className={styles.loadingWrapper} data-testid="insight-info-loading">
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: 40,
              },
            },
          ]}
        />
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: 72,
              },
            },
          ]}
        />
        <SkeletonLoader
          areas={[
            {
              styles: {
                width: '100%',
                height: 312,
              },
            },
          ]}
        />
      </div>
    );
  }

  if (!data?.data?.insight) {
    return (
      <div className={styles.wrapper}>
        <img
          className={styles.image}
          alt=""
          src="/app/static/images/empty-state/radio-telescope.png"
        />
      </div>
    );
  }

  return (
    <LegacyInsightsDrawerContent
      insightsData={data.data}
      showEntityHeader={false}
      showReferenceMetadata={!isInsightDrawerHeaderEnabled}
      header={() =>
        isInsightDrawerHeaderEnabled ? (
          <InsightDrawerHeaderContent
            cardType={CardType.Scenario}
            context={
              <InsightDrawerCategoryRow
                entity={data.data.entity}
                insight={data.data.insight}
                variant="metadata"
              />
            }
            publishedAt={data.data.insight.created_at}
            title={
              data.data.insight.simple_title ?? data.data.insight.card.name
            }
            variant="scenario"
          />
        ) : (
          <div className={styles.header}>
            {iconConfig && (
              <InsightIconsRoot {...iconConfig} isStarred={false} />
            )}
            <h3 className={styles.title}>{data.data.insight.simple_title}</h3>
          </div>
        )
      }
    />
  );
};
